import React, { useState } from 'react';
import { Star, Clock, Calendar, Play, ExternalLink, Users, Film, X } from 'lucide-react';
import { useTMDBMovie, useStreamingProviders } from '../../hooks/useTMDBData';
import { useMeasuredWidth } from '../../hooks/useMeasuredWidth';
import { buildPosterSrcSet, tmdbIdOf } from '../../services/tmdbService';

export default function EnhancedMovieCard({ 
  movie, 
  onDragStart, 
  isDraggable = false, 
  isStarred = false, 
  onToggleStar, 
  showStarButton = true,
  className = '',
  reviewAggregate = null,
  onClick = null,
  showFullDetails = false,
  onRemove = null,
  showRemoveButton = false
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Only fetch TMDB data for non-placeholder movies
  const { tmdbData, loading: tmdbLoading } = useTMDBMovie(
    movie.isPlaceholder ? null : movie.title,
    movie.isPlaceholder ? null : movie.year,
    movie.isPlaceholder ? null : tmdbIdOf(movie)
  );
  const { providers } = useStreamingProviders(movie.isPlaceholder ? null : tmdbData?.id);

  const handleDragStart = (e) => {
    if (isDraggable && onDragStart) {
      onDragStart(e, movie);
    }
  };

  const handleStarClick = (e) => {
    e.stopPropagation();
    if (onToggleStar) {
      onToggleStar(movie.id);
    }
  };

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    if (onRemove && window.confirm(`Are you sure you want to remove "${movie.title}" from your collection?`)) {
      onRemove(movie.id);
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(movie);
    }
  };

  const formatRuntime = (minutes) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatReleaseDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).getFullYear();
  };

  // For placeholder movies, use original data only. For TMDB movies, prefer TMDB data with fallbacks
  const displayData = movie.isPlaceholder ? {
    // Placeholder movies: use original data exactly as entered
    title: movie.title,
    year: movie.year,
    runtime: movie.runtime,
    genre: movie.genres?.[0] || movie.genre,
    rating: movie.rating,
    plot: movie.plot,
    posterUrl: movie.posterUrl, // Will be null for placeholders
    trailerUrl: movie.trailerUrl, // Will be null for placeholders
    director: movie.director,
    writers: movie.writers,
    stars: movie.stars,
    originalLanguage: movie.originalLanguage
  } : {
    // TMDB movies: use TMDB data if available, fallback to original movie data
    title: movie.title,
    year: formatReleaseDate(tmdbData?.releaseDate) || movie.year,
    runtime: tmdbData?.runtime || movie.runtime,
    genre: tmdbData?.genres?.[0] || movie.genre,
    rating: tmdbData?.rating || movie.rating,
    plot: tmdbData?.plot,
    posterUrl: tmdbData?.posterUrl,
    trailerUrl: tmdbData?.trailerUrl,
    director: tmdbData?.director,
    writers: tmdbData?.writers,
    stars: tmdbData?.stars,
    originalLanguage: tmdbData?.originalLanguage
  };

  // Measured rather than derived from the grid's breakpoints - see
  // useMeasuredWidth for why computing this from Tailwind classes is a trap.
  const [posterBoxWidth, setPosterBoxRef] = useMeasuredWidth();

  return (
    <div
      className={`${movie.isPlaceholder ? movie.color : 'bg-gray-700'} border-2 border-orange-600 rounded-lg overflow-hidden hover:opacity-90 transition-all ${
        isDraggable ? 'cursor-move' : 'cursor-pointer'
      } ${className}`}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      onClick={handleCardClick}
    >
      {/* Poster Image */}
      {displayData.posterUrl && !imageError && (
        <div className="relative" ref={setPosterBoxRef}>
          <img
            src={displayData.posterUrl}
            srcSet={posterBoxWidth ? buildPosterSrcSet(displayData.posterUrl) : undefined}
            sizes={posterBoxWidth ? `${posterBoxWidth}px` : undefined}
            alt={`${displayData.title} poster`}
            className={`w-full h-48 object-cover transition-opacity ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
          
          {/* Loading placeholder */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-600 flex items-center justify-center">
              <Film className="text-gray-400" size={32} />
            </div>
          )}

          {/* Action Buttons Overlay */}
          <div className="absolute top-2 right-2 flex gap-1">
            {showRemoveButton && (
              <button
                onClick={handleRemoveClick}
                className="p-1 rounded-full bg-black bg-opacity-50 text-red-400 hover:text-red-300 transition-colors"
                title="Remove from collection"
              >
                <X size={16} />
              </button>
            )}
            {showStarButton && (
              <button
                onClick={handleStarClick}
                className={`p-1 rounded-full bg-black bg-opacity-50 transition-colors ${
                  isStarred 
                    ? 'text-yellow-400 hover:text-yellow-300' 
                    : 'text-gray-400 hover:text-yellow-400'
                }`}
                title={isStarred ? 'Unstar movie' : 'Star movie'}
              >
                <Star size={16} fill={isStarred ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>

          {/* Trailer Button */}
          {displayData.trailerUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(displayData.trailerUrl, '_blank');
              }}
              className="absolute bottom-2 right-2 p-2 bg-red-600 hover:bg-red-500 rounded-full transition-colors"
              title="Watch Trailer"
            >
              <Play size={14} fill="white" />
            </button>
          )}

          {/* TMDB Rating Badge */}
          {displayData.rating && (
            <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-yellow-400 text-xs font-bold px-2 py-1 rounded">
              ⭐ {displayData.rating.toFixed(1)}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-3 space-y-2">
        {/* Action Buttons (if no poster) */}
        {(!displayData.posterUrl || imageError) && (showStarButton || showRemoveButton) && (
          <div className="flex justify-end gap-1 mb-2">
            {showRemoveButton && (
              <button
                onClick={handleRemoveClick}
                className="p-1 rounded-full text-red-400 hover:text-red-300 transition-colors"
                title="Remove from collection"
              >
                <X size={16} />
              </button>
            )}
            {showStarButton && (
              <button
                onClick={handleStarClick}
                className={`p-1 rounded-full transition-colors ${
                  isStarred 
                    ? 'text-yellow-400 hover:text-yellow-300' 
                    : 'text-gray-400 hover:text-yellow-400'
                }`}
                title={isStarred ? 'Unstar movie' : 'Star movie'}
              >
                <Star size={16} fill={isStarred ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>
        )}

        {/* Title */}
        <h3 className="font-bold text-orange-300 text-sm leading-tight">
          {displayData.title}
        </h3>
        
        {/* Year and Runtime */}
        <div className="flex items-center justify-between text-xs text-orange-200">
          <div className="flex items-center space-x-1">
            <Calendar size={12} />
            <span>{displayData.year}</span>
          </div>
          
          {displayData.runtime && (
            <div className="flex items-center space-x-1">
              <Clock size={12} />
              <span>{formatRuntime(displayData.runtime)}</span>
            </div>
          )}
        </div>

        {/* Genre and Language */}
        <div className="flex items-center justify-between text-xs text-gray-300">
          {displayData.genre && <span>{displayData.genre}</span>}
          {displayData.originalLanguage && displayData.originalLanguage !== 'en' && (
            <span className="bg-gray-600 px-2 py-1 rounded text-xs">
              {displayData.originalLanguage.toUpperCase()}
            </span>
          )}
        </div>

        {/* Plot (if showing full details) */}
        {showFullDetails && displayData.plot && (
          <div className="text-xs text-gray-300 leading-relaxed">
            {displayData.plot.length > 150 
              ? `${displayData.plot.substring(0, 150)}...` 
              : displayData.plot
            }
          </div>
        )}

        {/* Director and Stars */}
        {showFullDetails && (displayData.director || displayData.stars?.length > 0) && (
          <div className="text-xs text-gray-300 space-y-1">
            {displayData.director && (
              <div className="flex items-center space-x-1">
                <Users size={10} />
                <span><strong>Dir:</strong> {displayData.director}</span>
              </div>
            )}
            {displayData.stars?.length > 0 && (
              <div className="flex items-center space-x-1">
                <Star size={10} />
                <span><strong>Stars:</strong> {displayData.stars.slice(0, 2).join(', ')}</span>
              </div>
            )}
          </div>
        )}

        {/* Review Aggregate */}
        {reviewAggregate && (
          <div className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">
            <div className="flex items-center justify-between">
              <span>📝 {reviewAggregate.reviewCount} review{reviewAggregate.reviewCount !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center justify-between text-xs mt-1">
              <span>⚰️ {reviewAggregate.avgOverall}</span>
              <span>😱 {reviewAggregate.avgScariness}</span>
            </div>
          </div>
        )}

        {/* Streaming Services */}
        {providers && (providers.flatrate || providers.buy || providers.rent) && (
          <div className="space-y-1">
            {providers.flatrate && (
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-gray-400">Stream:</span>
                {providers.flatrate.slice(0, 3).map((provider) => (
                  <span
                    key={provider.provider_id}
                    className="text-xs bg-green-600 text-white px-2 py-1 rounded-full"
                    title={provider.provider_name}
                  >
                    {provider.provider_name}
                  </span>
                ))}
              </div>
            )}
            {providers.buy && (
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-gray-400">Buy:</span>
                {providers.buy.slice(0, 2).map((provider) => (
                  <span
                    key={provider.provider_id}
                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full"
                    title={provider.provider_name}
                  >
                    {provider.provider_name}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Loading indicator */}
        {tmdbLoading && (
          <div className="text-xs text-gray-400 italic">
            Loading movie details...
          </div>
        )}

        {/* External Link */}
        {tmdbData?.id && (
          <div className="flex justify-end">
            <a
              href={`https://www.themoviedb.org/movie/${tmdbData.id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-orange-300 transition-colors"
              title="View on TMDB"
            >
              <ExternalLink size={12} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
